import { useEffect, useRef } from 'react';

import { useLocale } from 'hooks';
import { useI18n } from 'context/i18n';
import { interpolate } from 'utils/string';

export function GiftCardTermsText({ className, onTermsClick }: { className?: string; onTermsClick: () => void }) {
  // Cuando se use solo este componente, mover modal de condiciones aqui dentro
  const { t, profileData } = useI18n();
  const { country } = useLocale();
  const giftCardTermsRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (giftCardTermsRef.current !== null) {
      // find first span in giftCardTermsRef
      const span = giftCardTermsRef.current?.querySelector('span');
      // add click event to span
      if (span) span.addEventListener('click', onTermsClick);
    }
    // onTermsClick may change but we cannot memoize it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // este check no sera necesario cuando se cambie a que todos los paises tengan politica de privacidad de esta forma. lo que si cambiara es el sufijo de la tag de traduccion
  if (country !== 'US') return null;

  return (
    <span
      ref={giftCardTermsRef}
      className={className ?? ''}
      dangerouslySetInnerHTML={{
        __html: interpolate(
          t(
            'generico',
            'gift.card.terms.us',
            'I\'ve read and accepted the <span> Terms and conditions</span> and <a href="<TAG privacy_policy_url>" target="_blank" rel="noreferrer">Privacy policy</a>.',
          ),
          {
            '<TAG privacy_policy_url>': profileData?.urlPrivacyPolicy,
          },
        ),
      }}
    />
  );
}

export function PrivacyPolicyText({ className, isCamper = true }: { className?: string; isCamper?: boolean }) {
  const { t, profileData } = useI18n();
  const { country } = useLocale();
  // Lo ideal es que generico.privacy.policy tenga valor

  return country === 'US' ?
      <span
        className={className}
        dangerouslySetInnerHTML={{
          __html: interpolate(
            t(
              'generico',
              'privacy.policy.us',
              'I\'ve read and accepted the <a href="<TAG privacy_policy_url>"> privacy policy</a> to become a member of The Walking Society and to receive commercial communications from Camper, including Newsletters and/or SMS',
            ),
            {
              '<TAG privacy_policy_url>': profileData?.urlPrivacyPolicy,
              '<TAG terms_url>': profileData?.urlTermsAndConditions,
            },
          ),
        }}
      />
    : <span className={className}>
        {t('compra.formulario', 'acepto.las')}&nbsp;
        <a href={profileData?.urlPrivacyPolicy} target="_blank" rel="noreferrer">
          {t('generico', 'privacy.conditions', 'privacy conditions')}
        </a>
        {isCamper ?
          <>
            &nbsp;
            {t('compra.formulario', 'become.member')}
          </>
        : null}
      </span>;
}

export function TermsAndConditionsText({ className }: { className?: string }) {
  const { t, profileData } = useI18n();
  const { country } = useLocale();
  // este check no sera necesario cuando se cambie a que todos los paises tengan politica de privacidad de esta forma. lo que si cambiara es el sufijo de la tag de traduccion
  if (country !== 'US') return null;

  return (
    <span
      className={className ?? ''}
      dangerouslySetInnerHTML={{
        __html: interpolate(
          t(
            'generico',
            'terms.and.conditions.us',
            'I\'ve read and accepted the <a href="<TAG terms_url>"> terms and conditions</a> and <a href="<TAG privacy_policy_url>"> privacy policy</a>.',
          ),
          {
            '<TAG terms_url>': profileData?.urlTermsAndConditions,
            '<TAG privacy_policy_url>': profileData?.urlPrivacyPolicy,
          },
        ),
      }}
    />
  );
}
export function TakebackTermsAndConditionsText({ className }: { className?: string }) {
  const { t, profileData } = useI18n();
  const { country, language } = useLocale();
  // este check no sera necesario cuando se cambie a que todos los paises tengan politica de privacidad de esta forma. lo que si cambiara es el sufijo de la tag de traduccion
  if (country !== 'US') return null;
  return (
    <span
      className={className ?? ''}
      dangerouslySetInnerHTML={{
        __html: interpolate(
          t(
            'takeback',
            'terms.and.conditions.us',
            'I would like to become a member of the The Walking Society by agreeing to Camper’s <a href="<TAG privacy_policy_url>"> Privacy Policy</a> and <a href="<TAG takeback_url>"> Terms and Conditions of Take back</a>.',
          ),
          {
            '<TAG takeback_url>': `/html/legal/takeback/TC_TB_${language.toUpperCase()}.pdf`,
            '<TAG privacy_policy_url>': profileData?.urlPrivacyPolicy,
          },
        ),
      }}
    />
  );
}

export function ShoeCareTermsAndConditionsText({ className }: { className?: string }) {
  const { t } = useI18n();
  const { language } = useLocale();

  return (
    <span className={className}>
      {t('compra.formulario', 'acepto.las')}&nbsp;
      <a href={`https://www.camper.com/html/legal/events-members-tc/Shoe_Care_MD_${language}.pdf`} target="_blank" rel="noreferrer">
        {t('generico', 'shoe.care.terms')}
      </a>
    </span>
  );
}
